// Ripple Console 入口 / Console entrypoint.
// 职责：启动引导（auth）-> 按 URL 路径分派到 console / report 模式 -> 绑定所有特性模块。
// Responsibility: bootstrap auth -> dispatch on URL path -> wire all feature modules.

import "@/styles/main.css";

import { bootstrap } from "@/features/auth-bootstrap";
import { initCardList } from "@/features/card-list";
import { openDemoDetail, startDemoSimulation } from "@/features/demo-detail";
import { loadDemos } from "@/features/demo-catalog";
import { enterGraphViewerMode, isGraphPath } from "@/features/graph-viewer/index";
import { initLogTerminal } from "@/features/log-terminal/terminal";
import { initModeToggle } from "@/features/mode-toggle";
import {
  createAndOpenTaskDetail,
  openTaskDetail,
  openTaskReports,
  startTaskSimulation,
} from "@/features/task-detail";
import {
  enterDemoReportViewerMode,
  enterReportListMode,
  enterReportViewerMode,
  isDemoReportPath,
  isReportListPath,
  isReportPath,
} from "@/features/report-viewer";
import { renderSessionChip } from "@/features/session-chip";
import { initWorkbenchToolbar } from "@/features/workbench-toolbar";
import { setHidden } from "@/lib/dom";
import { log } from "@/lib/logging";
import { state } from "@/state/store";

// SPA 启动入口 / SPA boot entrypoint.
async function main(): Promise<void> {
  // graph 模式与 report 模式一样不走 bootstrap()：cookie 随 API 请求自动发送，
  // 未登录时 401 → AuthError → 空态提示登录。
  // Graph mode skips bootstrap() like report mode: cookies ride along with API
  // requests; unauthenticated 401s surface as the AuthError empty state.
  if (isGraphPath()) {
    enterGraphViewerMode();
    return;
  }
  if (isDemoReportPath()) {
    enterDemoReportViewerMode();
    return;
  }
  if (isReportListPath()) {
    enterReportListMode();
    return;
  }
  if (isReportPath()) {
    enterReportViewerMode();
    return;
  }

  // Stage E：认证状态后台校验；首屏先按本地 UI 快照渲染，避免刷新阻塞。
  // Stage E: auth status reconciles in the background; first paint uses a
  // local UI snapshot to avoid blocking refreshes.
  void bootstrap()
    .then(() => {
      window.dispatchEvent(new CustomEvent("ripple:auth-updated"));
    })
    .catch((error) => {
      log.error("bootstrap failed", { error: String(error) });
      window.dispatchEvent(new CustomEvent("ripple:auth-updated"));
    });
  renderSessionChip();

  enterConsoleMode();
}

// 控制台模式的组装 / Console-mode wiring.
function enterConsoleMode(): void {
  setHidden("console-root", false);
  setHidden("report-root", true);

  initModeToggle();
  initCardList({
    onDetails: (target) => {
      if (target.kind === "demo") openDemoDetail({ mode: target.mode, id: target.id });
      if (target.kind === "task") void openTaskDetail(target.id);
    },
  });
  initWorkbenchToolbar({
    onCreateTask: async () => {
      await createAndOpenTaskDetail({ mode: state.mode });
    },
    onStartSimulation: async (target) => {
      if (target.kind === "demo") {
        await startDemoSimulation({ mode: target.mode, id: target.id });
        return;
      }
      await startTaskSimulation(target.id);
    },
    onOpenReport: (target) => {
      if (target.kind === "task") return openTaskReports(target.id);
      return undefined;
    },
  });

  void loadDemos();
  void initLogTerminal();
}

document.addEventListener("DOMContentLoaded", () => {
  void main();
});
